import { observer } from "mobx-react";
import RmaShipmentStore, { ILabelOrSlipsLink } from "../../../../stores/rma-shipment-store";
import GridLine from "../../../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import FontBody1 from "../../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../components/fonts/font-props";
import FontBody3 from "../../../../../../../components/fonts/font-body3/font-body3";
import AHref from "../../../../../../../components/ahref/ahref";
import '../../rma-page-shipments-card.scss';
import MissingShipmentValue from "../missing-shipment-value/missing-shipment-value";
import RmaPageStore from "../../../../stores/rma-page-store";
import useDashboardTranslation from "../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import ShipmentStatusChip from "../shipment-status-chip/shipment-status-chip";
import LabelOrSlipsCell from "../label-or-slips-cell/label-or-slips-cell";
import SlipsCell from "../slips-cell/slips-cell";

interface IProps {
    shipment: RmaShipmentStore;
    rmaPageStore: RmaPageStore;
};

const ShipmentWithAnyCarrierWithLabel: React.FC<IProps> = observer(({ shipment, rmaPageStore }) => {
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-returns-rma-card';

    const TrackingNumberLabel = shipment.isOutboundShipment ? getTranslation('SHIPMENT_COLUMN_TITLE_FOR_OUTBOUND_SHIPMENT', pageScope) : getTranslation('SHIPMENT_COLUMN_TITLE_FOR_TRACKING_NUMBER', pageScope);

    const labelContent = (): ILabelOrSlipsLink => {
        if (shipment.returnLabelUrl) {
            return {
                title: 'LABEL',
                link: {
                    label: 'VIEW_LABEL',
                    href: shipment.returnLabelUrl,
                },
                onLinkClick: () => rmaPageStore.auditDownloadLabel(shipment.id),
            };
        }

        return {
            title: 'VIEW_LABEL',
            link: {
                label: '--',
                href: null,
            }
        };
    }

    return (
        <GridLine
            settings={{
                columns: [{ width: 3 }, { width: 1.5 }, { width: 2 }, { width: 2 }, { width: 2 }, { width: 2 }, { width: 2 }, { width: 1 }, { width: 2 }]
            }}
            columns={[
                <GridLineItem>
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{shipment.returnMethodName}</FontBody1>
                </GridLineItem>,

                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{getTranslation('SHIPMENT_COLUMN_TITLE_FOR_CARRIER', pageScope)}</FontBody3>
                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{shipment.carrier ?? <MissingShipmentValue rmaPageStore={rmaPageStore} />}</FontBody3>
                </GridLineItem>,

                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{getTranslation('SHIPMENT_COLUMN_TITLE_FOR_SERVICE_LEVEL', pageScope)}</FontBody3>
                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{getTranslation('DATA_VALUE_NOT_APPLICABLE', pageScope)}</FontBody3>
                </GridLineItem>,

                <GridLineItem>
                    <LabelOrSlipsCell content={labelContent()} rmaPageStore={rmaPageStore} />
                </GridLineItem>,

                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{getTranslation('QR_BARCODE', pageScope)}</FontBody3>
                    {!!shipment.QRImage.url 
                        ? (
                            <AHref href={shipment.QRImage.url} target="_blank">
                                <FontBody3 color={FONT_COLOR.ACCENT1}>{getTranslation(shipment.QRImage.title, pageScope)}</FontBody3>
                            </AHref>
                        )
                        : <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{getTranslation('QR_NOT_GENERATED', pageScope)}</FontBody3>
                    }
                </GridLineItem>,

                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{TrackingNumberLabel}</FontBody3>
                    {!!shipment.trackingUrl 
                        ? (
                            <AHref href={shipment.trackingUrl} target="_blank">
                                <FontBody3 color={FONT_COLOR.ACCENT1}>{shipment.trackingNumber ?? <MissingShipmentValue rmaPageStore={rmaPageStore} />}</FontBody3>
                            </AHref>
                        )
                        : (
                            <div className="shipment-label-with-warning">
                                <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{shipment.trackingNumber ?? <MissingShipmentValue rmaPageStore={rmaPageStore} />}</FontBody3>
                            </div>
                        )
                    }
                </GridLineItem>,
                <GridLineItem>
                    <SlipsCell shipment={shipment} rmaPageStore={rmaPageStore} />
                </GridLineItem>,
                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>{getTranslation('ITEMS', pageScope)}</FontBody3>
                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{shipment.numberOfItems}</FontBody3>
                </GridLineItem>,

                <GridLineItem>
                    <FontBody3 color={FONT_COLOR.PRIMARY3_50}>{shipment.status ? <ShipmentStatusChip status={shipment.status} statusCode={shipment.statusCode} /> : <MissingShipmentValue rmaPageStore={rmaPageStore} />}</FontBody3>
                </GridLineItem>,
            ]} />
    );
});

export default ShipmentWithAnyCarrierWithLabel;